import {MiniCart} from "./MiniCart";
import {CartBar} from "./CartBar";
import {Add2CartDialogContent} from "./Add2CartDialogContent";
import {MultiAdd2CartDialogContent} from "./MultiAdd2CartDialogContent";
import {CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {SelectionManager} from "./SelectionManager";
import {BuyBarMenu} from "./BuyBarMenu";
import Overlay from "../Overlay";
import Polyfill from "../Polyfill";

/** The one cart processor of the page, created by the first handler below. */
let sharedCartProcessor: CartProcessor | null = null;

/**
 * Opens the format dialog for several images.
 *
 * The list goes to the server as a POST body; a selection of a few hundred
 * images would not fit into a URL.
 */
function openMultiDialog(cartProcessor: CartProcessor, items: {folder: string, file: string}[]) {
    if (!items || items.length === 0) {
        return;
    }
    const overlay: any = new Overlay();
    overlay.onLoad = () => {
        new MultiAdd2CartDialogContent(cartProcessor, overlay.myDialogElement, overlay.signal);
    };
    overlay.openOverlay(cartProcessor.urls.add2cartMultiDialogUrl, {
        method: 'POST',
        body: new URLSearchParams({items: JSON.stringify(items)})
    });
}

document.addEventListener('DOMContentLoaded', () => {
    try {
        let cartProcessor = new CartProcessor();
        sharedCartProcessor = cartProcessor;

        // the format dialog opened by the lightbox: the event names the dialog and its signal
        document.addEventListener(Events.prepareAdd2cartDialogContent, (e: Event) => {
            const detail = (e as CustomEvent).detail;
            new Add2CartDialogContent(cartProcessor, detail?.root ?? document, detail?.signal)
       });

        // @ts-ignore
        window.Eventgallery = window.Eventgallery || {};
        // @ts-ignore
        window.Eventgallery.cart = {
            openMultiDialog: (items: {folder: string, file: string}[]) => openMultiDialog(cartProcessor, items)
        };

        // a list, not the live collection: the bar of a component page moves to the end of the body
        const carts = Array.from(document.querySelectorAll<HTMLElement>('.eventgallery-minicart-container'));
        if (carts.length == 0) return

        for (const cart of carts) {
            new MiniCart(cartProcessor, cart, null);
            if (CartBar.isBar(cart)) {
                new CartBar(cartProcessor, cart);
            }
        }

        cartProcessor.loadCart();
    } catch (e) {
        console.log(e);
    }

})

/**
 * The buy mode of an event page: shows the cart controls on the thumbnails.
 *
 * The buy bar is pinned to the bottom of the viewport, the one bar of the
 * page: the cart button at its start is the toggle of the mini cart, which
 * CartBar.ts (the block above, run first) moved there. Its idle panel
 * carries the "Select images" button, its active panel the selection and the
 * "Done" button. The mode is remembered per event page in the session
 * storage, so a buyer who pages through an event does not have to switch
 * it on again on every page.
 *
 * The cart processor is shared with the block above through the module
 * scope, so the selection can open the format dialog for several images.
 */
document.addEventListener('DOMContentLoaded', () => {
    let container: HTMLElement = document.querySelector('.orderimages-container');
    if (!container) {
        return;
    }

    let hideButton: HTMLElement = container.querySelector('.orderimages-hide'),
        showButton: HTMLElement = container.querySelector('.orderimages-show'),
        idlePanel: HTMLElement = container.querySelector('.buy-bar-idle'),
        activePanel: HTMLElement = container.querySelector('.buy-bar-active');

    const storageKey = 'eventgallery.buymode.' + window.location.pathname;

    // "single", "selection" or "both": a body class so the styles can tell
    document.body.classList.add('eventgallery-buy-controls-' + (container.getAttribute('data-buy-controls') || 'both'));

    // fixed to the viewport, so it must not stay inside a positioned ancestor
    document.body.appendChild(container);

    // the page keeps its last row of thumbnails clear of the bar, whatever
    // height the bar takes; on a narrow screen it wraps to two rows
    const publishBarHeight = () => {
        document.documentElement.style.setProperty('--eg-buy-bar-height', container.offsetHeight + 'px');
    };
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(publishBarHeight).observe(container);
    } else {
        publishBarHeight();
    }

    if (sharedCartProcessor) {
        new SelectionManager(sharedCartProcessor, container);
    }

    // "Select all" and "Clear" in the menu of "More" on a phone (BuyBarMenu.ts)
    const menus = BuyBarMenu.bind(container);

    function setBuyMode(active: boolean) {
        container.classList.toggle('is-active', active);
        if (!active) {
            menus.forEach((menu) => menu.setOpen(false));
        }
        if (idlePanel) idlePanel.hidden = active;
        if (activePanel) activePanel.hidden = !active;
        document.body.classList.toggle('eventgallery-buy-mode', active);
        document.dispatchEvent(Polyfill.createNewEvent(Events.buyModeChanged, {active: active}));
    }

    function rememberBuyMode(active: boolean) {
        try {
            if (active) {
                window.sessionStorage.setItem(storageKey, '1');
            } else {
                window.sessionStorage.removeItem(storageKey);
            }
        } catch (e) {
            // storage may be blocked; the mode then lasts for this page only
        }
    }

    function wasBuyModeActive(): boolean {
        try {
            return window.sessionStorage.getItem(storageKey) === '1';
        } catch (e) {
            return false;
        }
    }

    showButton?.addEventListener('click', (e: Event) => {
        e.preventDefault();
        setBuyMode(true);
        rememberBuyMode(true);
    });
    hideButton?.addEventListener('click', (e: Event) => {
        e.preventDefault();
        setBuyMode(false);
        rememberBuyMode(false);
    });

    setBuyMode(wasBuyModeActive());
});
